import { ApolloError, useQuery, useSubscription } from '@apollo/client';

import {
  ErrorPage,
  Layout,
  PhoneMockupLayoutProvider,
  ToastProvider,
} from '@frontend/ui-library';
import { getIdFromUrl } from '@frontend/utils';
import { AnimatePresence } from 'framer-motion';
import { lazy, useState } from 'react';

import Error from '../pages/Error/Error';
import PaymentExpired from '../pages/PaymentExpired/PaymentExpired';
import PaymentCanceledByUser from '../pages/PaymentCanceledByUser/PaymentCanceledByUser';
import PaymentCanceledByMerchant from '../pages/PaymentCanceledByMerchant/PaymentCanceledByMerchant';
import RefundInitiated from '../pages/RefundInitiated/RefundInitiated';
import PaymentUnavailable from '../pages/PaymentUnavailable/PaymentUnavailable';
import UnsupportedCountry from '../pages/UnsupportedCountry/UnsupportedCountry';
import PaymentRateExpiredFlow from '../pages/flows/PaymentRateExpiredFlow';
import PaymentRejected from '../pages/PaymentRejected/PaymentRejected';
import PaymentSucceed from '../pages/PaymentSucceed/PaymentSucceed';
import Landing from '../pages/Landing/Landing';
import PaymentPending from '../pages/PaymentPending/PaymentPending';
import PaymentOverpaid from '../pages/PaymentOverpaid/PaymentOverpaid';
import KYTVerification from '../pages/TRMVerification/KYTVerification';
import BuyerInfo from '../pages/BuyerInfo/BuyerInfo';
import PaymentOptionsFlow from '../pages/flows/PaymentOptionsFlow';

import { Header } from '../components';
import { WidgetProvider } from '../context/WidgetContext';
import {
  GetCurrentPageQuery,
  GetCurrentPageQueryVariables,
  OnPageUpdatedSubscription,
  OnPageUpdatedSubscriptionVariables,
} from '../graphql/generated';
import { GET_CURRENT_PAGE } from '../graphql/queries';
import { SUBSCRIBE_TO_PAGE_UPDATE } from '../graphql/subscriptions';
import CountrySelection from '../pages/CountrySelection/CountrySelection';
import { mapPaymentPendingPageData } from '../pages/PaymentPending/utils';

import { scrollToTop } from '../utils';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { structuralSharing } from '@wagmi/core/query';
import PageToastReset from './PageToastReset';

const PaymentPage = lazy(() => import('../pages/Payment/Payment'));
const PaymentUnderpaidFlow = lazy(
  () => import('../pages/flows/PaymentUnderpaidFlow/PaymentUnderpaidFlow')
);

enum CancellationReason {
  TIMEOUT = 'timeout',
  BUYER_CANCELLATION = 'buyer_cancellation',
  MERCHANT_CANCELLATION = 'merchant_cancellation',
  REFUND = 'refund',
  UNSUPPORTED_COUNTRY = 'unsupported_country',
  MERCHANT_BILLS_DISABLED = 'merchant_bills_disabled',
}

const UNSUPPORTED_COUNTRY_KEY = 'unsupported-country';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      structuralSharing,
    },
  },
});

export default function Widget() {
  const id = getIdFromUrl();

  const [subscriptionData, setSubscriptionData] =
    useState<OnPageUpdatedSubscription['pageUpdated']>(null);

  const operationVariables = {
    id,
    type: 'bill',
  };

  const {
    data: initialData,
    loading,
    refetch,
    error: currentPageError,
  } = useQuery<GetCurrentPageQuery, GetCurrentPageQueryVariables>(
    GET_CURRENT_PAGE,
    {
      variables: operationVariables,
    }
  );

  useSubscription<
    OnPageUpdatedSubscription,
    OnPageUpdatedSubscriptionVariables
  >(SUBSCRIBE_TO_PAGE_UPDATE, {
    variables: operationVariables,
    onSubscriptionData: ({ subscriptionData }) => {
      const apiErrorTypesToBlockPageChange = ['invalid_request'];

      if (
        !subscriptionData.data?.pageUpdated ||
        (subscriptionData.data.pageUpdated.__typename === 'ErrorPage' &&
          apiErrorTypesToBlockPageChange.includes(
            subscriptionData.data.pageUpdated.pageData.type
          ))
      )
        return;

      setSubscriptionData(subscriptionData.data?.pageUpdated);
    },
  });

  const updatePageData = () => {
    setSubscriptionData(null);
    refetch(operationVariables);
  };

  const getStatusCode = (error: ApolloError | undefined) => {
    if (error?.networkError && 'statusCode' in error.networkError) {
      return error.networkError.statusCode.toString() as '404' | '500' | '403';
    }

    return null;
  };

  const errorStatusCode = getStatusCode(currentPageError);

  if (currentPageError && errorStatusCode)
    return <ErrorPage errorCode={errorStatusCode} />;

  type PageData =
    | GetCurrentPageQuery['currentPage']
    | OnPageUpdatedSubscription['pageUpdated'];

  const getPageByCancellationReason = (
    data: NonNullable<PageData>,
    reason: CancellationReason
  ) => {
    if (data.__typename !== 'CancelledPage') {
      return <Error />;
    }

    switch (reason) {
      case CancellationReason.TIMEOUT:
        return <PaymentExpired key="payment-expired" data={data} />;
      case CancellationReason.BUYER_CANCELLATION:
        return (
          <PaymentCanceledByUser
            key="canceled-by-user"
            data={data.cancelledPageData}
          />
        );
      case CancellationReason.MERCHANT_CANCELLATION:
        return (
          <PaymentCanceledByMerchant
            key="canceled-by-merchant"
            data={data.cancelledPageData}
          />
        );
      case CancellationReason.REFUND:
        return (
          <RefundInitiated data={data.cancelledPageData} key="refund-iniated" />
        );
      case CancellationReason.UNSUPPORTED_COUNTRY:
        return (
          <UnsupportedCountry
            key={UNSUPPORTED_COUNTRY_KEY}
            data={data.cancelledPageData}
          />
        );
      case CancellationReason.MERCHANT_BILLS_DISABLED:
        return (
          <PaymentUnavailable
            key="payment-unavailable"
            data={data.cancelledPageData}
          />
        );
    }
  };

  const renderPage = (data: PageData) => {
    if (!data) return <Error />;
    // keys are needed for framer-motion to animate between pages
    switch (data.__typename) {
      case 'EmailUpdatePage':
        return <Landing key="landing" data={data.pageData} />;
      case 'BuyerDetailsUpdatePage':
        return <BuyerInfo key="buyer-info" data={data.pageData} />;
      case 'CountryUpdatePage':
        return <CountrySelection key="country-update" data={data.pageData} />;
      case 'CurrenciesPage':
        return (
          <PaymentOptionsFlow
            key="payment-options-flow"
            data={data.pageData}
            onSelectionError={updatePageData}
          />
        );
      case 'PaymentPage':
        return <PaymentPage key="payment" data={data.pageData} />;
      case 'ErrorPage':
        return <Error key="error" />;
      case 'PaymentDetectedPage':
      case 'UnderpaidPaymentDetectedPage': {
        const pageData = mapPaymentPendingPageData(data);

        return <PaymentPending key="payment-pending" {...pageData} />;
      }

      case 'PaymentRejectedPage':
        return <PaymentRejected key="payment-rejected" data={data.pageData} />;
      case 'UnderpaymentConfirmedPage':
        return (
          <PaymentUnderpaidFlow key="payment-underpaid" data={data.pageData} />
        );
      case 'OverpaidPage':
        if (data.pageData.refundable)
          return (
            <PaymentOverpaid key="payment-overpaid" data={data.pageData} />
          );
        else return <PaymentSucceed key="success" data={data.pageData} />;
      case 'PaidPage':
        return <PaymentSucceed key="success" data={data.paidPageData} />;
      case 'PaymentExpiredPage':
        return (
          <PaymentRateExpiredFlow key="rate-expired" data={data.pageData} />
        );
      case 'KytVerificationStartedPage':
        return <KYTVerification key="kyt-verification" data={data.pageData} />;
      case 'CancelledPage':
        return getPageByCancellationReason(
          data,
          data.cancelledPageData?.reason as CancellationReason
        );

      default:
        return null;
    }
  };

  const getPageToastResetKey = (data: PageData) => {
    if (!data) return null;

    const pageName = 'pageName' in data ? data.pageName : '';
    const cancellationReason =
      data.__typename === 'CancelledPage'
        ? data.cancelledPageData?.reason ?? ''
        : '';

    return [data.__typename, pageName, cancellationReason]
      .filter(Boolean)
      .join(':');
  };

  const activePage = subscriptionData || initialData?.currentPage || null;
  const activePageKey = getPageToastResetKey(activePage);

  return (
    <WidgetProvider>
      <ToastProvider>
        <PageToastReset pageKey={activePageKey}>
          <Layout header={<Header />} loading={loading}>
            <QueryClientProvider client={queryClient}>
              <PhoneMockupLayoutProvider>
                <AnimatePresence mode="wait" onExitComplete={scrollToTop}>
                  {renderPage(activePage)}
                </AnimatePresence>
              </PhoneMockupLayoutProvider>
            </QueryClientProvider>
          </Layout>
        </PageToastReset>
      </ToastProvider>
    </WidgetProvider>
  );
}
